import FormInput from '@c/forms/controls/FormInput';
import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect from '@c/forms/controls/FormSelect';
import FormTextarea from '@c/forms/controls/FormTextarea';
import PageSection from '@c/listing/shared/PageSection';
import Button from '@ui/Button';
import Checkbox from '@ui/Checkbox';
import RadioButton from '@ui/RadioButton';
import Select from '@ui/Select';
import Toggle from '@ui/Toggle';
import useBikes from '@util/hooks/useBikes';
import useBrandOptions from '@util/hooks/useBrandOptions';
import { useAuth } from 'context/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Sparkles, Undo2 } from 'lucide-react';
import { bikeTypes, ENGINE_SIZES, VEHICLE_CATEGORIES } from 'models/product';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { stripHtml } from 'string-strip-html';
import { useCreateListing } from '../_context';
import { useProductAI } from '../_hooks/useProductAI';

const LazilyLoadedCreateListingEditor = dynamic(
  () => import('@c/ckeditor/create-listing'),
  {
    ssr: false,
  }
);
export const ProductInfo = ({
  otherBrand,
  setOtherBrand,
  isOtherBrand,
  setIsOtherBrand,
}: {
  otherBrand: string;
  setOtherBrand: (value: string) => void;
  isOtherBrand: boolean;
  setIsOtherBrand: (value: boolean) => void;
}) => {
  const { form, product } = useCreateListing();
  const { userDoc } = useAuth();
  const title = form.watch('title');
  const descriptionPlain = form.watch('description');
  const descriptionHtml = form.watch('description_html');

  const [manualEntry, setManualEntry] = useState(false);
  const [useRichText, setUseRichText] = useState(
    form.watch('description_html') ? true : false
  );

  const { titleState, descState, enhanceTitle, enhanceDescription } =
    useProductAI({
      form,
      product,
      useRichText,
    });

  const { brandOptions } = useBrandOptions(
    product.ride_types.includes('Cycling')
      ? 'cycling'
      : product.category === 'Bike Parts'
      ? 'parts'
      : product.category === 'Riding Gear'
      ? 'gear'
      : undefined
  );

  const { bikeYears, bikeMakes, bikeModelsByMake } = useBikes(
    [JSON.stringify(form.watch('year')) ?? ''],
    [form.watch('make') ?? ''],
    product.ride_types as ('Dirt Bikes' | 'ATV' | 'UTV' | 'Street')[]
  );

  useEffect(() => {
    if (!product.brand) {
      const foundBrand = brandOptions.find(
        (b) =>
          product.category3?.includes(b.value) ||
          product.category2?.includes(b.value)
      );
      if (foundBrand) {
        form.setValue('brand', foundBrand.value);
      }
    }
  }, [product.category2, product.category3, product.brand, brandOptions, form]);

  const titleCharsRemaining = Math.max(0, 21 - (title?.length || 0));
  const titleButtonDisabled =
    (title?.length ?? 0) <= 20 || titleState.status === 'loading';

  const descCharsRemaining = Math.max(0, 21 - (descriptionPlain?.length || 0));
  const descriptionButtonIsDisable =
    (descriptionPlain?.length ?? 0) <= 20 || descState.status === 'loading';

  const maxChars = useMemo(() => {
    return userDoc?.roles?.warehouse ? 200 : 80;
  }, [userDoc?.roles]);

  return (
    <PageSection title="Product Info" className={'my-8'}>
      <div className="flex flex-col gap-x-16 gap-y-16 sm:flex-row sm:gap-y-0">
        <div className="flex w-full flex-col gap-y-8">
          <FormLabel
            value="Title"
            required
            errorMessage={form.formState.errors.title?.message}
          >
            <ShimmerWrapper
              active={titleState.status === 'loading'}
              borderRadius="1.2rem"
            >
              <FormInput
                {...form.register('title', {
                  required: true,
                })}
                maxLength={maxChars}
                placeholder="AGV Valentino Rossi Turtle Edition Helmet"
              />
            </ShimmerWrapper>

            <div className="mt-4 flex w-full items-center justify-between text-brand-gray">
              <span>Max. {maxChars} characters</span>
              <span
                data-tooltip-id="ai-tooltip"
                data-tooltip-content={
                  titleButtonDisabled && titleState.status !== 'loading'
                    ? `Write ${titleCharsRemaining} more character${
                        titleCharsRemaining !== 1 ? 's' : ''
                      } to enable this feature`
                    : undefined
                }
              >
                <Button
                  text={
                    titleState.status === 'loading'
                      ? 'Improving...'
                      : titleState.status === 'updated'
                      ? 'Undo'
                      : 'Improve with AI'
                  }
                  leadingIcon={
                    titleState.status === 'loading' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : titleState.status === 'updated' ? (
                      <Undo2 size={14} />
                    ) : (
                      <Sparkles size={14} />
                    )
                  }
                  type="secondary"
                  className="!px-10 !py-4 !text-[1.5rem] !font-semibold !text-white"
                  onClick={enhanceTitle}
                  disabled={titleButtonDisabled}
                />
              </span>
            </div>
          </FormLabel>

          <FormLabel
            value="Description"
            required
            errorMessage={form.formState.errors.description?.message}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-x-4">
                <span className="text-[1.4rem] text-zinc-700">
                  Editor Mode:
                </span>
                <Toggle
                  offLabel="Plain Text"
                  onLabel="Rich Text"
                  enabled={useRichText}
                  onChange={() => setUseRichText(!useRichText)}
                />
              </div>
            </div>

            <ShimmerWrapper
              active={descState.status === 'loading'}
              borderRadius={useRichText ? '0.5rem' : '1.6rem'}
            >
              {useRichText ? (
                <LazilyLoadedCreateListingEditor
                  html={descriptionHtml ?? ''}
                  onChange={(data) => {
                    form.setValue('description_html', data);
                    form.setValue('description', stripHtml(data).result);
                  }}
                />
              ) : (
                <FormTextarea
                  {...form.register('description', {
                    required: true,
                  })}
                  placeholder="Really good quality helmet, never used, still in box."
                  value={descriptionPlain ?? ''}
                  onChange={(e) => {
                    form.setValue('description', e.target.value);
                  }}
                />
              )}
            </ShimmerWrapper>

            <div className="mt-4 flex justify-end">
              <span
                data-tooltip-id="ai-tooltip"
                data-tooltip-content={
                  descriptionButtonIsDisable && descState.status !== 'loading'
                    ? `Write ${descCharsRemaining} more character${
                        descCharsRemaining !== 1 ? 's' : ''
                      } to enable this feature`
                    : undefined
                }
              >
                <Button
                  text={
                    descState.status === 'loading'
                      ? 'Improving...'
                      : descState.status === 'updated'
                      ? 'Undo'
                      : 'Improve with AI'
                  }
                  leadingIcon={
                    descState.status === 'loading' ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : descState.status === 'updated' ? (
                      <Undo2 size={14} />
                    ) : (
                      <Sparkles size={14} />
                    )
                  }
                  type="secondary"
                  className="!px-10 !py-4 !text-[1.5rem] !font-semibold !text-white"
                  onClick={enhanceDescription}
                  disabled={descriptionButtonIsDisable}
                />
              </span>
            </div>
          </FormLabel>

          <div className="w-full sm:w-[50rem]">
            <FormLabel value="SKU">
              <FormInput {...form.register('sku')} placeholder="1234567890" />
              <span className="text-[1.4rem] text-zinc-600">
                Used for internal record keeping
              </span>
            </FormLabel>
          </div>

          <div className="w-full sm:w-[50rem]">
            <FormLabel
              value="MPN"
              errorMessage={form.formState.errors.mpn?.message}
            >
              <FormInput
                {...form.register('mpn', {
                  setValueAs: (value: string) =>
                    typeof value === 'string'
                      ? value
                          .split(',')
                          .map((mpn) => mpn.trim())
                          .filter(Boolean)
                      : value,
                })}
                placeholder="MPN1, MPN2, MPN3"
              />
              <span className="text-[1.4rem] text-zinc-600">
                Manufacturer product numbers of all items included in the
                listing, separated by commas
              </span>
            </FormLabel>
          </div>

          <div className="w-full sm:w-[50rem]">
            {!VEHICLE_CATEGORIES.includes(product.category) && (
              <div className="flex flex-col gap-y-6">
                {!isOtherBrand && product.brand !== 'OEM' && (
                  <FormLabel
                    value="Brand"
                    required
                    errorMessage={form.formState.errors.brand?.message}
                  >
                    <FormSelect
                      allowEmpty={false}
                      options={[
                        ...brandOptions,
                        {
                          label: 'Other',
                          value: 'other',
                          id: 'other',
                        },
                      ]}
                      control={form.control}
                      name={'brand'}
                      placeholder="Select Brand"
                      defaultValue={product?.brand}
                    />
                  </FormLabel>
                )}

                <div className="mt-4">
                  {form.watch('brand') === 'other' && (
                    <FormLabel value="Other Brand" required>
                      <FormInput
                        value={otherBrand}
                        onChange={(e) => {
                          setOtherBrand(e.target.value);
                        }}
                      />
                    </FormLabel>
                  )}
                </div>

                <Checkbox
                  selected={manualEntry}
                  onChange={() => {
                    setManualEntry(!manualEntry);
                    setIsOtherBrand(!isOtherBrand);
                  }}
                  label="Brand Not Listed"
                />
                {product.category === 'Bike Parts' && (
                  <Toggle
                    label="Is OEM?"
                    enabled={product.brand === 'OEM'}
                    onChange={() => {
                      if (product.brand === 'OEM') {
                        form.setValue('brand', '');
                      } else {
                        form.setValue('brand', 'OEM');
                      }
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {VEHICLE_CATEGORIES.includes(product.category) && (
            <div className="w-full">
              <div className="flex flex-col gap-y-6">
                {product.category === 'Dirt Bikes' && (
                  <FormLabel
                    value="Bike Type"
                    required
                    errorMessage={form.formState.errors.category1?.message}
                  >
                    <FormSelect
                      allowEmpty={false}
                      options={bikeTypes.options?.map((bt: any) => ({
                        id: bt.value,
                        label: bt.value,
                        value: bt.value,
                      }))}
                      control={form.control}
                      name={'category1'}
                      defaultValue={product?.category1}
                    />
                  </FormLabel>
                )}

                <div className="flex items-center gap-x-4">
                  <FormLabel
                    value="Year"
                    required
                    errorMessage={form.formState.errors.year?.message}
                  >
                    <Select
                      allowEmpty={false}
                      options={bikeYears.map((year) => ({
                        label: `${year}`,
                        value: year,
                        id: `${year}`,
                      }))}
                      onChange={(option: any) => {
                        form.setValue('year', option.value);
                      }}
                      value={form.watch('year')?.toString() ?? ''}
                    />
                  </FormLabel>

                  <FormLabel value="Make">
                    <Select
                      allowEmpty={false}
                      options={bikeMakes.map((make) => ({
                        label: make,
                        value: make,
                        id: make,
                      }))}
                      onChange={(option: any) => {
                        form.setValue('make', option.value);
                      }}
                      value={form.watch('make')}
                    />
                  </FormLabel>
                </div>
                <FormLabel value="Model" required>
                  <Select
                    allowEmpty={false}
                    options={
                      bikeModelsByMake[
                        form.watch('make') as keyof typeof bikeModelsByMake
                      ]?.map(({ model }) => ({
                        label: model,
                        value: model,
                        id: model,
                      })) ?? [
                        {
                          label: 'Select year & make first',
                          value: '',
                          id: 'placeholder',
                        },
                      ]
                    }
                    onChange={(option: any) => {
                      form.setValue('model', option.value);
                    }}
                    value={form.watch('model')}
                    disabled={
                      !Boolean(form.watch('year')) &&
                      !Boolean(form.watch('make'))
                    }
                  />
                </FormLabel>

                {product.category === 'Dirt Bikes' && (
                  <div className="w-full sm:w-[60rem]">
                    <FormLabel value="Engine Size">
                      <Select
                        allowEmpty={false}
                        options={ENGINE_SIZES.map((size) => ({
                          label: size,
                          value: +size,
                          id: size,
                        }))}
                        onChange={(option: any) => {
                          form.setValue('engine', option.value);
                        }}
                        value={form.watch('engine')?.toString() ?? ''}
                      />
                    </FormLabel>
                  </div>
                )}
              </div>
            </div>
          )}

          {manualEntry && (
            <div className="sm:w-[50rem]">
              <FormLabel value="Other Brand?" labelClasses="my-4">
                <FormInput
                  placeholder={'Brand'}
                  value={product.brand === 'na' ? 'N/A' : product.brand}
                  onChange={(e) => form.setValue('brand', e.target.value)}
                />
              </FormLabel>
            </div>
          )}

          <div className="sm:w-[50rem]">
            <FormLabel
              value="Condition"
              required
              errorMessage={
                form.formState.errors.condition
                  ? 'Please select a condition'
                  : ''
              }
            >
              <div className="mt-8 flex gap-8">
                <RadioButton
                  label="New"
                  checked={form.watch('condition') === 'New'}
                  onClick={() => form.setValue('condition', 'New')}
                />

                <RadioButton
                  label="Used"
                  checked={form.watch('condition') === 'Used'}
                  onClick={() => form.setValue('condition', 'Used')}
                />
              </div>
            </FormLabel>
          </div>

          {product.category3 === 'Frames' && (
            <div className="sm:w-[50rem]">
              <FormLabel value="Title Included?">
                <div className="mt-8 flex gap-8">
                  <RadioButton
                    label="Included"
                    checked={
                      form.watch('specs')?.['Title Included'] === 'Included'
                    }
                    onClick={() =>
                      form.setValue('specs.Title Included', 'Included')
                    }
                  />

                  <RadioButton
                    label="Not Included"
                    checked={
                      !form.watch('specs')?.['Title Included'] ||
                      form.watch('specs')?.['Title Included'] === 'Not Included'
                    }
                    onClick={() =>
                      form.setValue('specs.Title Included', 'Not Included')
                    }
                  />
                </div>
              </FormLabel>
            </div>
          )}
        </div>

        <div className="hidden flex-col gap-y-6 sm:flex">
          <h3 className="text-[1.8rem] font-semibold text-black">
            Provide complete info about the product.
          </h3>

          <p className="text-[1.5rem] text-zinc-600">
            Enhance your product description to build trust with potential
            buyers.
          </p>
        </div>
      </div>
      <Tooltip id="ai-tooltip" style={{ zIndex: 50 }} />
    </PageSection>
  );
};

const ShimmerWrapper = ({
  active,
  borderRadius,
  children,
}: {
  active: boolean;
  borderRadius: string;
  children: React.ReactNode;
}) => (
  <div className="relative" style={{ borderRadius }}>
    <AnimatePresence>
      {active && (
        <motion.div
          className="pointer-events-none absolute -inset-[2px] z-0"
          style={{ borderRadius: `calc(${borderRadius} + 2px)` }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: 1,
            boxShadow: [
              '0 0 8px 2px rgba(232,145,126,0.4)',
              '0 0 16px 4px rgba(232,145,126,0.7)',
              '0 0 8px 2px rgba(232,145,126,0.4)',
            ],
          }}
          exit={{ opacity: 0 }}
          transition={{
            opacity: { duration: 0.3 },
            boxShadow: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
          }}
        />
      )}
    </AnimatePresence>
    <div className="relative z-[1]">{children}</div>
  </div>
);
